import { apiKeyClient } from "@better-auth/api-key/client";
import { oauthProviderClient } from "@better-auth/oauth-provider/client";
import { stripeClient } from "@better-auth/stripe/client";
import {
	adminClient,
	anonymousClient,
	genericOAuthClient,
	inferAdditionalFields,
	inferOrgAdditionalFields,
	jwtClient,
	lastLoginMethodClient,
	magicLinkClient,
	multiSessionClient,
	organizationClient,
	// phoneNumberClient,
	usernameClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

import { env, isBrowser } from "@/env/client";

import type { auth } from "./auth";

export const authClient = createAuthClient({
	baseURL: isBrowser ? window.location.origin : env.VITE_BASE_URL,
	plugins: [
		inferAdditionalFields<typeof auth>(),
		adminClient(),
		anonymousClient(),
		// phoneNumberClient(),
		magicLinkClient(),
		genericOAuthClient(),
		jwtClient(),
		organizationClient({
			teams: {
				enabled: true,
			},
			schema: inferOrgAdditionalFields<typeof auth>(),
		}),
		usernameClient(),
		apiKeyClient(),
		oauthProviderClient(),
		multiSessionClient(),
		lastLoginMethodClient(),
		stripeClient({ subscription: true }),
	],
});

export const {
	useActiveOrganization,
	useSession,
	getSession,
	signUp,
	signIn,
	signOut,
	linkSocial,
	unlinkAccount,
	listAccounts,
	updateUser,
	requestPasswordReset: forgetPassword,
	resetPassword,
	organization: { create: createOrganization, getFullOrganization, inviteMember },
	apiKey,
	revokeSession,
	multiSession: { listDeviceSessions, setActive: setActiveSession, revoke: revokeDeviceSession },
	subscription,
} = authClient;

export const useAuthedSession = useSession as typeof useSession & {
	data: NonNullable<Awaited<ReturnType<typeof useSession>>["data"]>;
};

export type UseSessionReturnData = NonNullable<Awaited<ReturnType<typeof useSession>>["data"]>;

export type AuthErrorCode = keyof typeof authClient.$ERROR_CODES;

export function transformAuthResponse<T>(response: {
	data: T | null;
	error: { message?: string } | null;
}): T {
	if (response.error) {
		throw new Error(response.error.message ?? "Unknown error");
	}
	return response.data as T;
}
